// Reusable right-side slide-over drawer for the KPI detail surface and any
// future drawer needs. Sticky header (`backdrop-blur-xs`) keeps action
// buttons reachable while the user scrolls long content. Esc and backdrop
// click both close; focus trap + body scroll-lock are delegated to
// useFocusTrap. Slide-in animation is dropped under prefers-reduced-motion.

import { useId, useRef, type ReactNode } from "react";
import { X } from "lucide-react";
import Button from "./Button";
import { useFocusTrap } from "../../hooks/useFocusTrap";
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";

// `activity` is the 420px schedule ActivityDrawer width (spec §10.1).
// Additive — the existing sm/md/lg keys + the `md` default are
// unchanged, so existing consumers and the committed width test are
// untouched.
export type SlideOverDrawerWidth = "sm" | "md" | "lg" | "activity";

export interface SlideOverDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  width?: SlideOverDrawerWidth;
  /** When true the drawer stays mounted but goes inert —
   *  pointer-events blocked, aria-hidden set, focus trap suspended.
   *  Used when a layer-2 modal mounts above the drawer so the drawer's
   *  state (scroll, expanded rows, etc.) doesn't reset on close. */
  inert?: boolean;
  /** When true the drawer renders WITHOUT a scrim backdrop and as a
   *  non-modal panel: no overlay, `aria-modal` omitted, and the focus
   *  trap does NOT lock body scroll — the main content stays visible
   *  and interactive to the left. Used by the schedule ActivityDrawer
   *  (spec §10.1), where the drawer is a contextual detail panel, not
   *  a modal interruption. Default (unset) preserves the modal
   *  scrim/aria-modal/scroll-lock behaviour exactly. */
  noBackdrop?: boolean;
}

const WIDTH_CLASS: Record<SlideOverDrawerWidth, string> = {
  sm: "max-w-[480px]",
  md: "max-w-[600px]",
  lg: "max-w-[800px]",
  activity: "max-w-[420px]",
};

export default function SlideOverDrawer({
  isOpen,
  onClose,
  title,
  subtitle,
  actions,
  children,
  width = "md",
  inert = false,
  noBackdrop = false,
}: SlideOverDrawerProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const reducedMotion = usePrefersReducedMotion();

  // enabled: isOpen && !inert re-arms the trap on the closed → open
  // transition. The drawer is eagerly mounted while closed (panelRef is null
  // then), so keying `enabled` on !inert alone would fire the trap once at
  // mount, bail on the null ref, and never re-run — leaving Escape/
  // scroll-lock/auto-focus dead. noBackdrop ⇒ contextual panel: keep the
  // focus trap (Esc + Tab cycle + restore-focus) but DON'T lock body scroll,
  // so the main content stays visible and scrollable to the left (spec §10.1).
  useFocusTrap(panelRef, {
    onEscape: onClose,
    enabled: isOpen && !inert,
    lockScroll: !noBackdrop,
  });

  if (!isOpen) return null;

  const animationClass = reducedMotion ? "" : "animate-slide-in";
  // When inert, suppress backdrop click-close so a layer-2 modal click
  // can't accidentally dismiss the underlying drawer. aria-hidden tells
  // AT to skip the drawer surface.
  const containerAriaHidden = inert ? true : undefined;

  return (
    <div
      className={`fixed inset-0 z-50 flex justify-end ${
        // inert blocks all pointer events; noBackdrop lets clicks pass
        // through the empty area to the still-interactive main content
        // (the panel itself re-enables pointer events below).
        inert || noBackdrop ? "pointer-events-none" : ""
      }`}
      aria-hidden={containerAriaHidden}
      data-inert={inert ? "true" : undefined}
    >
      {!noBackdrop && (
        <div
          data-testid="slide-over-backdrop"
          className="absolute inset-0 bg-slate-900/50 dark:bg-slate-950/70"
          onClick={inert ? undefined : onClose}
          aria-hidden="true"
        />
      )}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal={inert || noBackdrop ? undefined : "true"}
        aria-labelledby={titleId}
        className={`pointer-events-auto relative flex h-full w-full flex-col overflow-hidden bg-white shadow-soft-lg dark:shadow-soft-lg-dark dark:border-l dark:border-slate-700 dark:bg-slate-800 ${WIDTH_CLASS[width]} ${animationClass}`}
      >
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-slate-200 bg-white/90 px-6 py-4 backdrop-blur-xs dark:border-slate-700 dark:bg-slate-800/90">
          <div className="min-w-0 flex-1">
            <h2
              id={titleId}
              className="text-h3 text-slate-900 dark:text-slate-100"
            >
              {title}
            </h2>
            {subtitle && (
              <div className="mt-1 text-body-sm text-slate-500 dark:text-slate-400">
                {subtitle}
              </div>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {actions}
            <Button
              variant="ghost"
              iconOnly
              size="sm"
              icon={<X className="h-5 w-5" />}
              aria-label="Close drawer"
              onClick={onClose}
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>
      </div>
    </div>
  );
}
